import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpResponseHeaders,
  NamingStrategy
} from '@/data/protocols/http'
import { getSessionId } from '@/data/services/login/session-id-store'
import { i18nTranslationHelper } from '@/i18n'
import { settings } from '@/main/config/settings'
import {
  getRecaptchaSiteKey,
  getRecaptchaToken,
  setRecaptchaSiteKey,
  setRecaptchaToken
} from '@/presentation/contexts/recaptcha/recaptcha-token-store'
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'

const isInternalRequest = (url?: string): boolean =>
  !!url && !!settings.BASE_URL && url.startsWith(settings.BASE_URL)

axios.interceptors.response.use((response: AxiosResponse) => {
  if (response.data && response.headers['content-type'] === 'application/json') {
    response.data = camelizeKeys(response.data)
  }
  return response
})

axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const newConfig = { ...config }

  if (isInternalRequest(newConfig.url)) {
    const language = i18nTranslationHelper.getCurrentLanguage()
    if (language) {
      newConfig.headers['Accept-Language'] = language
    }

    const recaptchaToken = getRecaptchaToken()
    if (recaptchaToken) {
      newConfig.headers['X-Recaptcha-Token'] = recaptchaToken
      setRecaptchaToken(null)
    }

    const recaptchaSiteKey = getRecaptchaSiteKey()
    if (recaptchaSiteKey) {
      newConfig.headers['X-Recaptcha-Site-Key'] = recaptchaSiteKey
      setRecaptchaSiteKey(null)
    }

    const sessionId = getSessionId()
    if (sessionId) {
      newConfig.headers['X-Session-Id'] = sessionId
      newConfig.headers['X-Correlation-Id'] = sessionId + '_' + crypto.randomUUID()
    }
  }

  if (!config.headers['Content-Type'] || config.headers['Content-Type'] === 'application/json') {
    if (config.params) {
      newConfig.params =
        newConfig.paramsNamingStrategy === NamingStrategy.snakeCase
          ? decamelizeKeys(newConfig.params)
          : camelizeKeys(newConfig.params)
    }
    if (config.data && !(config.data instanceof FormData)) {
      newConfig.data = decamelizeKeys(config.data)
    }
  }

  return newConfig
})

export class AxiosHttpClient implements HttpClient {
  async request(data: HttpRequest): Promise<HttpResponse> {
    let axiosResponse: AxiosResponse
    try {
      axiosResponse = await axios.request({
        url: data.url,
        method: data.method,
        data: data.body,
        headers: data.headers,
        paramsNamingStrategy: data.paramsNamingStrategy,
        params: data.params,
        withCredentials: data.withCredentials,
        onUploadProgress:
          data.onUploadProgress &&
          (progressEvent => data.onUploadProgress!(((progressEvent.progress || 0) * 100) | 0)),
        signal: data.signal ? data.signal : undefined,
        responseType: data.responseType
      })
    } catch (error) {
      axiosResponse = (error as AxiosError).response!
    }

    return {
      statusCode: axiosResponse?.status,
      body: axiosResponse?.data,
      headers: axiosResponse?.headers as HttpResponseHeaders,
      correlationId: axiosResponse?.config?.headers?.['X-Correlation-Id']
    }
  }
}
